import { FC, ReactElement, ReactNode, useMemo, useState } from 'react';
import { Button, Col, Menu, Row } from 'antd';
import { capitalCase } from 'change-case';
import _ from 'lodash';
import { LucidePiggyBank, LucideShapes, Tags as TagsIcon } from 'lucide-react';
import { useQueryState } from 'nuqs';
import {
    TbBuildingBank,
    TbBuildingFactory2,
    TbCalendarDollar,
    TbChartDonut,
    TbChartPie,
    TbCloudBolt,
    TbCrosshair,
    TbCurrencyDollar,
    TbFoldUp,
    TbLeaf,
    TbMapPin2,
    TbNumber123,
    TbTagStarred,
    TbUsersGroup,
    TbWorldBolt,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    DropdownMenuPanel,
    FilterButtonGroup,
    FilterButtonGroupSegment,
    getSearchFilterOperator,
    getSearchFilterOperatorOptions,
    searchFiltersQuery,
    updateSearchFilterEnvelope,
    updateSearchFilterOperator,
    type SearchFilterOperator,
} from '@/shared';
import { PlatformSelect as Select } from '@/shared/components/forms';

import type { SearchFilterValueSelectionMode } from '../../filterFieldMetadata';
import SearchFilterFieldPicker from '../../SearchFilterFieldPicker';

interface AttributeFilterItemProps {
    fieldTriggerWrapper?: (trigger: ReactElement) => ReactElement;
    fieldLabel?: string;
    handleDelete: (path: string) => void;
    label: string;
    onOperatorChange?: (operator: SearchFilterOperator) => void;
    onValueChange?: (value: unknown) => void;
    operator?: SearchFilterOperator;
    path: string;
    value: unknown;
    valueOptions?: Array<{ label: string; value: string }>;
    valueOptionsLoading?: boolean;
    valueSelectionMode?: SearchFilterValueSelectionMode;
}

const iconStyle = { verticalAlign: '-0.075rem', marginRight: '0.25rem' } as const;

const keyNameMap: Record<string, string> = {
    assetClasses: 'Asset Type',
    attributes: 'Attributes',
    employeeHeadcount: 'Employee Headcount',
    esg: 'ESG',
    exchanges: 'Exchanges',
    fiscalYearEnd: 'Fiscal Year-End',
    fundraising: 'Fundraising',
    fundamentals: 'Fundamentals',
    identifierCodes: 'Identifier Codes',
    industries: 'Industries',
    institutionalOwnership: 'Institutional Ownership',
    location: 'Locations (HQ)',
    screener: 'Stock Screener',
    sectors: 'Sectors',
    technologies: 'Technologies',
    watchlists: 'Watchlists',
    webTraffic: 'Website Traffic',
    workspaceTags: 'Tags',
};

const keyIconMap: Record<string, ReactNode> = {
    assetClasses: <LucideShapes size='0.75rem' style={iconStyle} />,
    attributes: <TbTagStarred size='0.75rem' style={iconStyle} />,
    employeeHeadcount: <TbUsersGroup size='0.75rem' style={iconStyle} />,
    esg: <TbLeaf size='0.75rem' style={iconStyle} />,
    exchanges: <TbBuildingBank size='0.75rem' style={iconStyle} />,
    fiscalYearEnd: <TbCalendarDollar size='0.75rem' style={iconStyle} />,
    fundraising: <LucidePiggyBank size='0.75rem' style={iconStyle} />,
    fundamentals: <TbFoldUp size='0.75rem' style={iconStyle} />,
    identifierCodes: <TbNumber123 size='0.75rem' style={iconStyle} />,
    industries: <TbBuildingFactory2 size='0.75rem' style={iconStyle} />,
    institutionalOwnership: <TbChartDonut size='0.75rem' style={iconStyle} />,
    location: <TbMapPin2 size='0.75rem' style={iconStyle} />,
    screener: <TbCurrencyDollar size='0.75rem' style={iconStyle} />,
    sectors: <TbChartPie size='0.75rem' style={iconStyle} />,
    technologies: <TbCloudBolt size='0.75rem' style={iconStyle} />,
    watchlists: <TbCrosshair size='0.75rem' style={iconStyle} />,
    webTraffic: <TbWorldBolt size='0.75rem' style={iconStyle} />,
    workspaceTags: <TagsIcon size='0.75rem' style={iconStyle} />,
};

const arrayBackedFields = new Set([
    'assetClasses',
    'attributes',
    'employeeHeadcount',
    'exchanges',
    'fiscalYearEnd',
    'fundraising',
    'industries',
    'sectors',
    'watchlists',
    'webTraffic',
    'workspaceTags',
]);

const isPresent = (value: unknown) => value !== '' && value !== null && value !== undefined;
const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const recordValue = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

const hasRangeValue = (value: unknown) => {
    const range = recordValue(value);

    return isPresent(range.min) || isPresent(range.max);
};

const createOperatorSegment = (label = 'contains'): FilterButtonGroupSegment => ({
    key: `operator-${label}`,
    label,
    maxWidth: 16 * 8,
});

const createValueSegment = (
    key: string,
    label: ReactNode,
    maxWidth: FilterButtonGroupSegment['maxWidth'] = 16 * 12,
): FilterButtonGroupSegment => ({
    key,
    label,
    maxWidth,
});

const renderRangeSegments = (value: unknown): FilterButtonGroupSegment[] => {
    const range = recordValue(value);

    return [
        createValueSegment('between', 'Between', 16 * 8),
        createValueSegment('min', isPresent(range.min) ? String(range.min) : 'Any', 16 * 7),
        createValueSegment('and', 'And', 16 * 5),
        createValueSegment('max', isPresent(range.max) ? String(range.max) : 'Any', 16 * 7),
    ];
};

const renderObjectValueSegments = (value: Record<string, unknown>): FilterButtonGroupSegment[] =>
    Object.entries(value)
        .filter(([, nestedValue]) => {
            if (Array.isArray(nestedValue)) {
                return nestedValue.length > 0;
            }

            if (isRecord(nestedValue)) {
                return Object.values(nestedValue).some(isPresent);
            }

            return isPresent(nestedValue);
        })
        .map(([key, nestedValue]) => {
            if (Array.isArray(nestedValue)) {
                return createValueSegment(key, `${capitalCase(key)} (${nestedValue.join(', ')})`);
            }

            if (isRecord(nestedValue)) {
                return createValueSegment(
                    key,
                    `${capitalCase(key)} (${Object.values(nestedValue).filter(isPresent).join(', ')})`,
                );
            }

            return createValueSegment(key, `${capitalCase(key)} (${String(nestedValue)})`);
        });

const renderValueSegments = (value: unknown, operatorLabel: string): FilterButtonGroupSegment[] => {
    if (typeof value === 'string' || typeof value === 'number') {
        return [createOperatorSegment(operatorLabel), createValueSegment('value', value)];
    }

    const valueRecord = recordValue(value);
    if (['sic', 'cusip', 'lei', 'cei', 'isin', 'figi'].some((key) => isPresent(valueRecord[key]))) {
        return [
            createOperatorSegment(operatorLabel),
            ...Object.entries(valueRecord)
                .filter(([, identifierValue]) => isPresent(identifierValue))
                .map(([key, identifierValue]) =>
                    createValueSegment(key, `${key.toUpperCase()} (${String(identifierValue)})`),
                ),
        ];
    }

    if (Array.isArray(value)) {
        return [createOperatorSegment(operatorLabel), createValueSegment('values', value.join(', '))];
    }

    if (Array.isArray(valueRecord.annualRevenue)) {
        return [
            createValueSegment('annualRevenue', 'Annual Revenue'),
            createOperatorSegment(operatorLabel),
            createValueSegment('annualRevenue-values', valueRecord.annualRevenue.join(', ')),
        ];
    }

    if (hasRangeValue(value)) {
        return renderRangeSegments(value);
    }

    if (['country', 'stateTerritory', 'city', 'zipCode'].some((key) => isPresent(valueRecord[key]))) {
        return [
            createOperatorSegment(operatorLabel),
            ...(isPresent(valueRecord.country)
                ? [createValueSegment('country', `Country (${String(valueRecord.country)})`)]
                : []),
            ...(isPresent(valueRecord.stateTerritory)
                ? [createValueSegment('stateTerritory', `State / Territory (${String(valueRecord.stateTerritory)})`)]
                : []),
            ...(isPresent(valueRecord.city) ? [createValueSegment('city', `City (${String(valueRecord.city)})`)] : []),
            ...(isPresent(valueRecord.zipCode)
                ? [createValueSegment('zipCode', `Zip Code (${String(valueRecord.zipCode)})`)]
                : []),
        ];
    }

    if (['environmental', 'social', 'governance'].some((key) => isPresent(valueRecord[key]))) {
        return Object.entries(valueRecord).flatMap(([key, range]) => [
            createValueSegment(key, capitalCase(key), 16 * 8),
            ...renderRangeSegments(range),
        ]);
    }

    const entireCategories = Array.isArray(valueRecord.entireCategories) ? valueRecord.entireCategories : [];
    const individualTechnologies = Array.isArray(valueRecord.individualTechnologies)
        ? valueRecord.individualTechnologies
        : [];
    if (entireCategories.length || individualTechnologies.length) {
        return [
            createOperatorSegment(operatorLabel),
            ...(entireCategories.length
                ? [createValueSegment('categories', `Categories (${entireCategories.join(', ')})`)]
                : []),
            ...(individualTechnologies.length
                ? [createValueSegment('technologies', `Technologies (${individualTechnologies.join(', ')})`)]
                : []),
        ];
    }

    if (isRecord(value)) {
        return [createOperatorSegment(operatorLabel), ...renderObjectValueSegments(value)];
    }

    return [];
};

const getEditableValuesFromValue = (value: unknown) => {
    if (Array.isArray(value)) {
        return value.map(String);
    }

    if (isPresent(value) && typeof value !== 'object') {
        return [String(value)];
    }

    return [];
};

type EditableObjectLeaf = {
    label: string;
    path: string;
    value: unknown;
};

const getEditableObjectLeaves = (value: Record<string, unknown>, prefix = ''): EditableObjectLeaf[] =>
    Object.entries(value).flatMap(([key, nestedValue]) => {
        const nestedPath = prefix ? `${prefix}.${key}` : key;

        if (isRecord(nestedValue)) {
            return getEditableObjectLeaves(nestedValue, nestedPath);
        }

        return [{ label: capitalCase(nestedPath.replaceAll('.', ' ')), path: nestedPath, value: nestedValue }];
    });

const AttributeFilterItem: FC<AttributeFilterItemProps> = ({
    fieldTriggerWrapper,
    fieldLabel,
    label,
    path,
    value,
    valueOptions,
    valueOptionsLoading = false,
    valueSelectionMode,
    handleDelete,
    onOperatorChange,
    onValueChange,
    operator: controlledOperator,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [filters, setFilters] = useQueryState('filters', searchFiltersQuery);
    const [draftObjectValue, setDraftObjectValue] = useState<Record<string, unknown>>(() =>
        _.cloneDeep(recordValue(value)),
    );
    const [draftValues, setDraftValues] = useState<string[]>(() => getEditableValuesFromValue(value));
    const [fieldPickerOpen, setFieldPickerOpen] = useState(false);
    const [openValueSegmentKey, setOpenValueSegmentKey] = useState<string | null>(null);
    const displayValue = useMemo(() => {
        if (!valueOptions) {
            return value;
        }

        const labelById = new Map(valueOptions.map((option) => [option.value, option.label]));

        if (Array.isArray(value)) {
            return value.map((id) => labelById.get(String(id)) ?? String(id));
        }

        if (typeof value === 'string' || typeof value === 'number') {
            return labelById.get(String(value)) ?? value;
        }

        return value;
    }, [value, valueOptions]);
    const editableValues =
        Array.isArray(value) || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
    const compatibleFieldEntries = Object.entries(keyNameMap).filter(
        ([key]) => key === label || (Array.isArray(value) && arrayBackedFields.has(key)),
    );
    const usesLocalFieldPicker = !fieldTriggerWrapper && compatibleFieldEntries.length > 1;
    const operator = controlledOperator ?? getSearchFilterOperator(filters, path, value);
    const operatorOptions = getSearchFilterOperatorOptions(value);
    const operatorLabel = operatorOptions.find((option) => option.value === operator)?.label ?? 'Contains';

    const updateFilterValue = (nextValue: unknown) => {
        if (onValueChange) {
            onValueChange(nextValue);
            return;
        }

        setFilters((previousFilters: unknown) =>
            updateSearchFilterEnvelope(previousFilters, (currentFilters) => {
                const nextFilters = _.cloneDeep(currentFilters);

                _.set(nextFilters, path, nextValue);

                return nextFilters;
            }),
        );
    };

    const moveFilterToField = (nextField: string) => {
        if (nextField === label || !(Array.isArray(value) && arrayBackedFields.has(nextField))) {
            return;
        }

        setFilters((previousFilters: unknown) =>
            updateSearchFilterEnvelope(previousFilters, (currentFilters) => {
                const nextFilters = _.cloneDeep(currentFilters);
                const parentPath = path.split('.').slice(0, -1).join('.');
                const nextPath = parentPath ? `${parentPath}.${nextField}` : nextField;

                _.unset(nextFilters, path);
                _.set(nextFilters, nextPath, value);

                return nextFilters;
            }),
        );
    };

    const fieldOverlay = (
        <SearchFilterFieldPicker
            groupLabel='properties'
            items={compatibleFieldEntries.map(([key, name]) => ({
                group: 'Company Filters',
                icon: keyIconMap[key],
                id: key,
                label: name,
            }))}
            onSelect={(nextField) => {
                moveFilterToField(nextField);
                setFieldPickerOpen(false);
            }}
            selectedId={label}
        />
    );

    const resolvedValueSelectionMode = valueSelectionMode ?? (valueOptions ? 'multiple' : 'tags');
    const updateDraftObjectLeaf = (leaf: EditableObjectLeaf, nextValue: unknown) => {
        const normalizeNextValue = () => {
            const values = Array.isArray(nextValue)
                ? nextValue
                : nextValue === undefined || nextValue === null
                  ? []
                  : [nextValue];

            if (Array.isArray(leaf.value)) {
                const numericValues = leaf.value.some((value: unknown) => typeof value === 'number');

                return values.map((item) => (numericValues ? Number(item) : item));
            }
            if (typeof leaf.value === 'boolean') {
                return values[0] === true || values[0] === 'true';
            }
            if (typeof leaf.value === 'number') {
                return Number(values[0]);
            }

            return values[0] ?? '';
        };

        setDraftObjectValue((currentValue) => {
            const nextObjectValue = _.cloneDeep(currentValue);

            _.set(nextObjectValue, leaf.path, normalizeNextValue());

            return nextObjectValue;
        });
    };
    const editableObjectLeaves = getEditableObjectLeaves(draftObjectValue);
    const valueOverlay = (
        <DropdownMenuPanel style={{ width: 16 * 18, padding: 0 }}>
            <div onClick={(event) => event.stopPropagation()} style={{ padding: '0.75rem' }}>
                <small style={{ color: token.colorTextTertiary, display: 'block', marginBottom: '0.25rem' }}>
                    Filter Value
                </small>
                <div style={{ color: token.colorText, fontWeight: 500, marginBottom: '0.5rem' }}>
                    {fieldLabel ?? keyNameMap[label] ?? capitalCase(label)}
                </div>
                {editableValues ? (
                    <>
                        <Select
                            loading={valueOptionsLoading}
                            mode={resolvedValueSelectionMode === 'single' ? undefined : resolvedValueSelectionMode}
                            options={valueOptions}
                            value={resolvedValueSelectionMode === 'single' ? draftValues[0] : draftValues}
                            onChange={(nextValue) => {
                                const nextValues = Array.isArray(nextValue)
                                    ? nextValue.map(String)
                                    : nextValue === undefined || nextValue === null
                                      ? []
                                      : [String(nextValue)];

                                setDraftValues(nextValues);
                            }}
                            placeholder='Add Values...'
                            style={{ width: '100%' }}
                            tokenSeparators={[',']}
                        />
                        <Row align='middle' style={{ marginTop: '0.5rem' }}>
                            <Col flex='auto' />
                            <Col flex='none'>
                                <Button
                                    size='small'
                                    type='primary'
                                    onClick={() => {
                                        if (!draftValues.length) {
                                            handleDelete(path);
                                            setOpenValueSegmentKey(null);

                                            return;
                                        }

                                        updateFilterValue(
                                            Array.isArray(value)
                                                ? draftValues
                                                : typeof value === 'number'
                                                  ? Number(draftValues[0])
                                                  : draftValues[0],
                                        );
                                        setOpenValueSegmentKey(null);
                                    }}
                                >
                                    Apply
                                </Button>
                            </Col>
                        </Row>
                    </>
                ) : (
                    <>
                        <div style={{ display: 'grid', gap: '0.5rem', maxHeight: 16 * 18, overflowY: 'auto' }}>
                            {editableObjectLeaves.map((leaf) => {
                                const leafValues = Array.isArray(leaf.value) ? leaf.value : [leaf.value];
                                const booleanValue = typeof leaf.value === 'boolean';

                                return (
                                    <div key={leaf.path}>
                                        <small
                                            style={{
                                                color: token.colorTextTertiary,
                                                display: 'block',
                                                marginBottom: 4,
                                            }}
                                        >
                                            {leaf.label}
                                        </small>
                                        <Select
                                            mode={booleanValue ? undefined : 'tags'}
                                            onChange={(nextValue) => updateDraftObjectLeaf(leaf, nextValue)}
                                            options={
                                                booleanValue
                                                    ? [
                                                          { label: 'Enabled', value: true },
                                                          { label: 'Disabled', value: false },
                                                      ]
                                                    : undefined
                                            }
                                            placeholder='Add Values...'
                                            style={{ width: '100%' }}
                                            tokenSeparators={[',']}
                                            value={booleanValue ? leaf.value : leafValues.map(String)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <Row align='middle' style={{ marginTop: '0.5rem' }}>
                            <Col flex='none'>
                                <Button danger size='small' type='text' onClick={() => handleDelete(path)}>
                                    Remove Filter
                                </Button>
                            </Col>
                            <Col flex='auto' />
                            <Col flex='none'>
                                <Button
                                    size='small'
                                    type='primary'
                                    onClick={() => {
                                        updateFilterValue(draftObjectValue);
                                        setOpenValueSegmentKey(null);
                                    }}
                                >
                                    Apply
                                </Button>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        </DropdownMenuPanel>
    );

    const operatorOverlay = (
        <DropdownMenuPanel selectedKeys={[operator]} style={{ width: 16 * 15, padding: 0 }}>
            <small style={{ color: token.colorTextTertiary, display: 'block', padding: '0.75rem 0.75rem 0.375rem' }}>
                Filter Operator
            </small>
            {operatorOptions.map((option) => (
                <Menu.Item
                    key={option.value}
                    onClick={() => {
                        const nextOperator = option.value as SearchFilterOperator;

                        if (onOperatorChange) {
                            onOperatorChange(nextOperator);
                            return;
                        }

                        void setFilters((previousFilters: unknown) =>
                            updateSearchFilterOperator(previousFilters, path, nextOperator),
                        );
                    }}
                >
                    {option.label}
                </Menu.Item>
            ))}
        </DropdownMenuPanel>
    );

    const valueSegments = renderValueSegments(displayValue, operatorLabel.toLowerCase()).map((segment) => {
        const isOperatorSegment = segment.key.startsWith('operator');
        const isStaticRangeSegment = segment.key === 'between' || segment.key === 'and';

        return {
            ...segment,
            dropdown: isOperatorSegment ? operatorOverlay : isStaticRangeSegment ? undefined : valueOverlay,
            open: isOperatorSegment || isStaticRangeSegment ? undefined : openValueSegmentKey === segment.key,
            onDropdownOpenChange:
                isOperatorSegment || isStaticRangeSegment
                    ? undefined
                    : (open: boolean) => {
                          setOpenValueSegmentKey(open ? segment.key : null);

                          if (open) {
                              setDraftValues(getEditableValuesFromValue(value));
                              setDraftObjectValue(_.cloneDeep(recordValue(value)));
                          }
                      },
        };
    });

    const segments: FilterButtonGroupSegment[] = [
        {
            dropdown: usesLocalFieldPicker ? fieldOverlay : undefined,
            icon: keyIconMap[label],
            key: 'name',
            label: fieldLabel ?? keyNameMap[label] ?? capitalCase(label),
            onDropdownOpenChange: usesLocalFieldPicker ? setFieldPickerOpen : undefined,
            open: usesLocalFieldPicker ? fieldPickerOpen : undefined,
            renderTrigger: fieldTriggerWrapper,
        },
        ...valueSegments,
    ];

    return <FilterButtonGroup segments={segments} onRemove={() => handleDelete(path)} />;
};

export default AttributeFilterItem;
